import React, {useState} from 'react';
import b_ from 'b_';

import {Icon, Button, Textinput, Checkbox} from 'client/common/components/lego';

import {useDenySurveyModeration, useSurvey} from 'client/common/hooks/survey';

import {isErrorResponse} from 'client/common/utils/api-client/types';
import I18N from 'assets/i18n/brandlift-project';

import {SurveySlug} from 'client/common/types';

import './moderation-deny.css';

const i18n = I18N.common.moderationDeny;
const b = b_.with('moderation-deny');

interface ModerationDenyProps {
    slug: SurveySlug;
    onDeny?: () => void;
}

type DenyErrorReason = 'ERROR_SEND_EMAIL';

export default function ModerationDeny(props: ModerationDenyProps) {
    const {slug, onDeny = () => {}} = props;

    const [error, setError] = useState<string>();

    const denySurveyModeration = useDenySurveyModeration(slug);

    async function onDenyModeration(withEmail: boolean, reasons: string[]) {
        const response = await denySurveyModeration(withEmail, reasons.filter(Boolean));

        if (isErrorResponse(response)) {
            const {reason} = response;

            return i18n.errors[reason as DenyErrorReason] || i18n.errors.default;
        }

        onDeny();
    }

    const {data: survey} = useSurvey(slug);

    const [withEmail, setWithEmail] = useState(true);
    const [reasons, setReasons] = useState<string[]>(['']);

    function addReason() {
        setReasons(reasons.concat(''));
    }

    function removeReason(index: number) {
        const newReasons = [...reasons];

        newReasons.splice(index, 1);
        setReasons(newReasons);
    }

    function changeReason(reason: string, index: number) {
        const newReasons = [...reasons];

        newReasons[index] = reason;
        setReasons(newReasons);
    }

    async function handleModerationCancel() {
        setError(undefined);

        const denyError = await onDenyModeration(withEmail, reasons);

        setError(denyError);
    }

    return (
        <div className={b()}>
            <h2 className={b('title')}>{i18n.title}</h2>
            <ul className={b('reasons')}>
                {reasons.map((reason, index) => {
                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index} className={b('reason')}>
                            <span className={b('reason-id')}>{index + 1}</span>
                            <Textinput
                                size='m'
                                value={reason}
                                view='default'
                                onChange={e => changeReason(e.target.value, index)}
                            />
                            {reasons.length > 1 && (
                                <Button
                                    className={b('button', {type: 'remove'})}
                                    size='m'
                                    view='clear'
                                    onClick={() => removeReason(index)}
                                    icon={(className: string) => (
                                        <Icon className={className} type='cross' />
                                    )}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
            <Button
                size='m'
                view='default'
                className={b('button', {type: 'add'})}
                iconLeft={(className: string) => <Icon className={className} type='plus' />}
                onClick={addReason}
            >
                {i18n.addReason}
            </Button>
            <Checkbox
                className={b('checkbox')}
                label={i18n.sendEmail}
                size='m'
                view='default'
                checked={withEmail}
                onChange={event => setWithEmail(event.target.checked)}
            />
            <Button
                size='m'
                width='max'
                view='action'
                className={b('button')}
                onClick={handleModerationCancel}
                disabled={survey?.state !== 'MODERATION'}
            >
                {i18n.deny}
            </Button>
            {error && <p className={b('error')}>{error}</p>}
        </div>
    );
}
